import { MxpPrepaymentAccountService, GetPrepaymentAccountResponse } from '../../../core/services/mxp-prepayment-account.service';
import { MxpUserService } from '../../../core/services/mxp-user.service';
import { Component, Input } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { GetByIdRequest, PaymentTypeEnum, MxpPaymentGrpcClient, MakeQRPaymentRequestV3, MakeNotificationPaymentRequestV3, MakeQRPaymentRequestV4, QrErrorResponseV4, PaymentSubtypeEnum, MakeNotificationPaymentRequestV4, MerchantStatusEnum, MerchantTypeEnum } from '@rezolved/mxp-proto';
import { first, take } from 'rxjs/operators';
import { AuthService } from '@rezolved/auth';
import { NavigationExtras, Router } from '@angular/router';
import Route from '../../../core/constants/Route';
import { BaseDropDownItem, BaseDropDownItemUtils } from '@rezolved/ui';
import { MxpSnackBarService } from '../../../core/services/mxp-snackbar.service';
import { ErrorHandlerService } from '../../../core/services/error-handler.service';
import { IAppState } from '../../../core/store/app.interface';
import { Store } from '@ngrx/store';
import { requestPayment } from '../../../core/store/actions/app.actions';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { NumberPatternEnum } from '../../../../../../../libs/ui/src/lib/enums/number-pattern.enum';
import { FloatValue, Timestamp } from '@ngx-grpc/well-known-types';
import { MerchantService } from '../../../core/services/mxp-merchant.service';
import { MatDialog } from '@angular/material/dialog';
import { EnrollmentSignatureDialogComponent } from '../../merchant-enrollment/enrollment-signature-dialog/enrollment-signature-dialog.component';

const MIN_VAL = 1;
const MINIMUM_VALUE_ERR_MESSAGE = `Minimum value is ${MIN_VAL}`;
const MINIMUM_DESC_LENGTH = 255;

@Component({
  selector: 'mxp-rezolve-request-payment-details',
  templateUrl: './payments-request.component.html',
  styleUrls: ['./payments-request.component.scss']
})
export class PaymentsRequestComponent {
  store = 'store';

  storeList: BaseDropDownItem[] = [];
  paymentTypes: BaseDropDownItem[];
  qrPayment = false;
  form: FormGroup;
  minValue = MIN_VAL;
  bulkFeatureDisabled = environment.bulkPaymentDisabled;
  pattern = NumberPatternEnum.POSITIVE_DECIMAL;
  @Input() maxDescLength = MINIMUM_DESC_LENGTH;
  showSpinner = false;
  reportLabel = 'DATE_RANGE';
  expiryDate: Date | undefined = undefined;
  currentType: PaymentTypeEnum = PaymentTypeEnum.QR;

  //Prepaymentinfo
  displayPrepaymentInfo = false; // we will replace this when we have the merchantTypeInfo
  paymentsLeft: number | undefined = 0;
  paymentsLeftExpiryDate: Date | undefined = new Date();


  validator(val: number | string) {
    return val < MIN_VAL ? MINIMUM_VALUE_ERR_MESSAGE : null;
  }

  getByIdRequest = (id: string) => {
    return new GetByIdRequest({ id });
  };

  constructor(
    private mxpPaymentGrpcClient: MxpPaymentGrpcClient,
    mxpUserService: MxpUserService,
    private authService: AuthService,
    private router: Router,
    private mxpSnackBarService: MxpSnackBarService,
    private errorHandlerService: ErrorHandlerService,
    private readonly state: Store<IAppState>,
    private mxpPrepaymentAccountService: MxpPrepaymentAccountService,
    private merchantService: MerchantService,
    private dialog: MatDialog
  ) {

    this.state.dispatch(requestPayment());
    this.paymentTypes = [
      { key: PaymentTypeEnum[PaymentTypeEnum.QR], value: PaymentTypeEnum.QR },
      { key: PaymentTypeEnum[PaymentTypeEnum.NOTIFICATION], value: PaymentTypeEnum.NOTIFICATION }
    ];
    this.storeList = BaseDropDownItemUtils.transformList([this.store]);
    this.form = new FormGroup({
      store: new FormControl({ value: this.store, disabled: true }, Validators.required),
      paymentType: new FormControl(PaymentTypeEnum.QR, Validators.required),
      contactInfo: new FormControl('', Validators.compose([Validators.maxLength(40)])),
      phone: new FormControl(''),
      amountQr: new FormControl('', Validators.compose([Validators.min(0.01), Validators.max(9999)]),),
      amountPn: new FormControl('', Validators.compose([Validators.min(0.01), Validators.max(9999)]),),
      description: new FormControl('', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[a-zA-Z0-9 $%,.:;'!@#&*()\/_]+$/)])),
      reference: new FormControl('', Validators.compose([Validators.maxLength(7), Validators.pattern('\\d+')])),
      extendedReference: new FormControl('', Validators.compose([Validators.maxLength(40)]))
    },
      {
        validators: this.toggleValidator(Validators.required, ['phone'])
      },
    );
    this.form.get('paymentType')?.valueChanges.subscribe((value) => {
      this.currentType = value;
      if (value === PaymentTypeEnum.NOTIFICATION) {
        this.form.get('phone')?.enable();
        this.form.get('amountPn')?.enable();
        this.form.get('amountQr')?.disable();
      } else {
        this.form.get('phone')?.disable();
        this.form.get('amountPn')?.disable();
        this.form.get('amountQr')?.enable();
      }
      this.form.get('phone')?.reset();
    });

    this.merchantService.merchant$.pipe(take(1)).subscribe(merchant => {
      if (merchant?.merchantType == MerchantTypeEnum.PREPAYMENT) {
        mxpPrepaymentAccountService.getPrepaymentAccount().subscribe({
          next: (prepaymentAccountInfo: GetPrepaymentAccountResponse) => {
            this.paymentsLeft = prepaymentAccountInfo.paymentsLeft;
            this.paymentsLeftExpiryDate = prepaymentAccountInfo.expiryDate ? new Date(prepaymentAccountInfo.expiryDate) : undefined;
            this.displayPrepaymentInfo = true;
          },
          error: (err) => {
            this.displayPrepaymentInfo = false;
            console.error("Error fetching prepayment account:", err);
          }
        });
      }
    });

    this.authService.user$.subscribe((user) => {
      if (!user) {
        return
      }
      mxpUserService.getCurrentUser().subscribe((userInfo) => {
        this.store = userInfo.storeName || this.store;
        this.storeList = BaseDropDownItemUtils.transformList([this.store]);
        this.form.get('store')?.patchValue(this.store);
      });
    });
  }

  get isQrPayment(): boolean {
    return this.form.get(`paymentType`)?.value === PaymentTypeEnum.QR;
  }

  dateChanged(date: Date) {
    this.expiryDate = date;
  }

  getExpiryDateTimeStamp(date: Date | undefined) {
    if (date == undefined) {
      return undefined;
    }
    else {
      return Timestamp.fromISOString((date as Date).toISOString());
    }
  }

  checkMerchantStatusAndSubmit(): void {
    this.merchantService.merchant$.pipe(take(1)).subscribe(merchant => {
      if (merchant?.merchantStatus === MerchantStatusEnum.ENROLLMENT_WITHOUT_SIGNATURE) {
        this.dialog.open(EnrollmentSignatureDialogComponent, {
          width: '550px',
          disableClose: true
        });
      } else {
        this.onSubmit(); // continue payment process
      }
    });
  }

  onSubmit() {
    this.showSpinner = true;
    this.authService.user$.pipe(first()).subscribe((user) => {
      if (user) {
        const referenceValue = this.form.get('reference')?.value;
        const commonRequest = {
          description: this.form.get('description')?.value,
          reference: referenceValue == '' ? 0 : referenceValue,
          extendedReference: this.form.get('extendedReference')?.value,
          expiryDate: this.getExpiryDateTimeStamp(this.expiryDate),
          contactInfo: this.form.get('contactInfo')?.value,
          paymentSubtype: PaymentSubtypeEnum.NORMAL
        };

        if (this.isQrPayment) {
          const amountValue = this.form.get('amountQr')?.value;
          const request = new MakeQRPaymentRequestV4({
            ...commonRequest,
            amount: amountValue == '' ? undefined : { value: amountValue } as FloatValue
          });

          this.mxpSnackBarService.wrapApiCall('SNACKBAR.MAKE_QR_PAYMENT', this.mxpPaymentGrpcClient.makeQRPaymentV4(request)).subscribe(
            (response) => {
              if (response.success) {
                const state = {
                  configuration: {
                    id: response.success.corePaymentId?.value,
                    qr: response.success.qrData?.value,
                    price: this.form.get('amountQr')?.value,
                    description: this.form.get('description')?.value,
                    reference: this.form.get('reference')?.value,
                    type: PaymentTypeEnum.QR,
                    expiryDate: this.getExpiryDateTimeStamp(this.expiryDate)
                  }
                }
                this.router.navigate([Route.PAYMENT, response.success.corePaymentId?.value], { state });
              }
              if (response.error) {
                const errorMessage = this.errorHandlerService.getErrorMessage(response.error.errorCode as number);
                this.mxpSnackBarService.showSnackbarMessage(errorMessage);
                this.showSpinner = false;
                return;
              }
            },
            (error) => {
              this.showSpinner = false
            },
            () => {
              this.showSpinner = false
            }
          );
        } else {
          const phoneValue = this.form.get('phone')?.value.substring(3);
          const request = new MakeNotificationPaymentRequestV4({
            ...commonRequest,
            phone: phoneValue,
            amount: this.form.get('amountPn')?.value
          });

          this.mxpSnackBarService.wrapApiCall('SNACKBAR.MAKE_NOTIFICATION_PAYMENT', this.mxpPaymentGrpcClient.makeNotificationPaymentV4(request)).subscribe(
            (response) => {
              if (response.success) {
                const state = {
                  configuration: {
                    id: response.success.corePaymentId,
                    price: this.form.get('amountPn')?.value,
                    description: this.form.get('description')?.value,
                    reference: this.form.get('reference')?.value,
                    type: PaymentTypeEnum.NOTIFICATION,
                    expiryDate: this.getExpiryDateTimeStamp(this.expiryDate),
                    mobileNumber: this.form.get('phone')?.value
                  }
                }
                this.router.navigate([Route.PAYMENT, response.success.corePaymentId], { state });
              }
              if (response.error) {
                const errorMessage = this.errorHandlerService.getErrorMessage(response.error.errorCode as number);
                this.mxpSnackBarService.showSnackbarMessage(errorMessage);
                this.showSpinner = false;
                return;
              }
            },
            (error) => {
              this.showSpinner = false
            },
            () => {
              this.showSpinner = false
            }
          );
        }
      }
    });
  }

  toggleValidator(validator: ValidatorFn, controls: string[] = []): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) return null;
      const formGroup = control as FormGroup;
      return formGroup && (this.currentType == PaymentTypeEnum.QR || controls.some((form) => !validator(formGroup.controls[form])))
        ? null
        : {
          toggleValidator: true
        };
    };
  }
}
